import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { PrimaryButton, SecondaryButton } from '@/components/index';

export interface MemberFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { identifier: string }) => Promise<void>;
}

interface FormData {
  identifier: string;
}

export const MemberFormDialog: React.FC<MemberFormDialogProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
    defaultValues: {
      identifier: ''
    }
  });

  const onFormSubmit = async (data: FormData) => {
    try {
      await onSubmit(data);
      reset();
      onClose();
    } catch (error) {
      // Error handling is done in the parent component/hook
      console.error('Form submission failed:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Member</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            name="identifier"
            control={control}
            rules={{ 
              required: 'Member identifier is required',
              validate: (value) => value.trim() !== '' || 'Member identifier cannot be empty'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Member Identifier"
                fullWidth
                variant="outlined"
                required
                error={!!errors.identifier}
                helperText={errors.identifier?.message || "Enter a unique identifier for the member"}
                sx={{ mb: 2 }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton
          onClick={handleSubmit(onFormSubmit)}
          type="submit"
          disabled={isSubmitting}
        >
          Add Member
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};